/**
 * Shared time axis for the Programming Language Activity chart.
 *
 * Used by BOTH renderers so they agree pixel-for-pixel on the x axis:
 *   - tools/skills-chart-generator.mjs  (static profile README SVGs)
 *   - src/components/LocalSkillsChart.vue (site chart)
 *
 * Piecewise-linear scale: the last `recentMonths` (default 18, snapped back to
 * a quarter start) get `recentShare` (default 0.28) of the plot width instead
 * of their linear share (~11% of a 14-year span). Both segments stay linear,
 * so areas inside a segment are still comparable; the renderers mark the
 * stretched segment with a background band, an axis-break glyph and a
 * "last N mo, xF scale" note, and label it quarterly.
 *
 * Plain ES module, no dependencies (Node imports it directly; webpack/babel
 * bundles it for the site).
 */

export const AXIS_DEFAULTS = Object.freeze({
  aiEraStart: '2025-10', // YYYY-MM; vertical "AI era" marker
  recentMonths: 18, // stretched trailing window (0 = plain linear axis)
  recentShare: 0.28, // share of the plot width given to that window
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ms = d => (d instanceof Date ? d.getTime() : Number(d));

/** 'YYYY-MM' (or 'YYYY-MM-DD') -> local Date at the 1st of that month. */
export function parseMonth(s) {
  const m = /^(\d{4})-(\d{2})/.exec(String(s || ''));
  if (!m) throw new Error(`expected YYYY-MM, got "${s}"`);
  return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

/** Start of the quarter that contains `d` (local time). */
function quarterStart(d) {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

/**
 * Build the x scale.
 * @param {object} o
 * @param {Date|number} o.min, o.max  data domain
 * @param {number} o.left, o.right    pixel range
 * @param {number} [o.recentMonths]   trailing window to stretch (0 disables)
 * @param {number} [o.recentShare]    share of the width for that window
 * @returns {{x:(d)=>number, invert:(px)=>number, min:number, max:number, left:number, right:number,
 *            breakDate:Date|null, breakX:number|null, factor:number, recentMonths:number}}
 */
export function makeTimeScale({ min, max, left, right, recentMonths = AXIS_DEFAULTS.recentMonths, recentShare = AXIS_DEFAULTS.recentShare }) {
  const t0 = ms(min);
  const t1 = ms(max);
  const w = right - left;
  const linear = {
    x: d => (t1 === t0 ? left : left + ((ms(d) - t0) / (t1 - t0)) * w),
    invert: px => (w <= 0 ? t0 : t0 + ((px - left) / w) * (t1 - t0)),
    min: t0, max: t1, left, right, breakDate: null, breakX: null, factor: 1, recentMonths: 0,
  };
  if (!(recentMonths > 0) || !(recentShare > 0 && recentShare < 1) || !(t1 > t0) || !(w > 0)) return linear;

  const end = new Date(t1);
  const breakDate = quarterStart(new Date(end.getFullYear(), end.getMonth() - recentMonths, 1));
  const tb = breakDate.getTime();
  if (!(tb > t0 && tb < t1)) return linear;
  const linearShare = (t1 - tb) / (t1 - t0);
  if (linearShare >= recentShare) return linear; // already wide enough: no stretch

  const breakX = left + (1 - recentShare) * w;
  const oldK = (breakX - left) / (tb - t0); // px per ms, compressed segment
  const newK = (right - breakX) / (t1 - tb); // px per ms, stretched segment
  return {
    x: d => {
      const t = ms(d);
      return t <= tb ? left + (t - t0) * oldK : breakX + (t - tb) * newK;
    },
    invert: px => (px <= breakX ? t0 + (px - left) / oldK : tb + (px - breakX) / newK),
    min: t0, max: t1, left, right, breakDate, breakX, factor: newK / oldK,
    // months actually stretched (window snapped back to a quarter start)
    recentMonths: (end.getFullYear() - breakDate.getFullYear()) * 12 + end.getMonth() - breakDate.getMonth(),
  };
}

/** "Oct 2026" */
export function formatMonthYear(d) {
  const x = new Date(ms(d));
  return `${MONTHS[x.getMonth()]} ${x.getFullYear()}`;
}

/** End-of-axis label: "Oct 2026", or "Oct 2026 (to date)" for an incomplete last month. */
export function endLabel(d, partial = false) {
  return partial ? `${formatMonthYear(d)} (to date)` : formatMonthYear(d);
}

/**
 * Is the series' last month incomplete? The stats series ends at the refresh
 * date, so its last month is month-to-date unless `asOf` (optional data
 * field, YYYY-MM-DD) says the data covers the whole month.
 * @param {Date|number} lastMonth  1st of the last month in the series
 * @param {string} [asOf]
 */
export function isPartialMonth(lastMonth, asOf) {
  const d = new Date(ms(lastMonth));
  if (!asOf) return true;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(asOf));
  if (!m) return true;
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0); // asOf on/after it = complete month
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() < lastDay.getTime();
}

/**
 * Tick marks + labels for a scale from makeTimeScale.
 *  - right edge: the end month ("Oct 2026"), right-aligned, always shown
 *  - compressed segment: years (every 1/2/3/5 years, whatever fits)
 *  - stretched segment: every quarter; Jan shows the year, the first tick shows
 *    "Apr '25" so the year is never ambiguous; labels that would collide are
 *    dropped (the tick mark stays) in priority order end > break/year > quarter
 * @param {number} [o.charWidth=5.5]  approx px per character at the label font size
 * @param {boolean} [o.partialEnd=false] the last month is incomplete: end label reads "Oct 2026 (to date)"
 * @returns {{labels: Array<{t:number,x:number,text:string,anchor:'start'|'middle'|'end'}>, ticks: number[]}}
 *          ticks = x positions of every tick mark (labelled or not)
 */
export function timeTicks(scale, { charWidth = 5.5, gap = 8, partialEnd = false } = {}) {
  const { min, max, left, right, breakDate } = scale;
  if (!(max > min)) return { labels: [], ticks: [] };
  const cand = [];
  const end = new Date(max);
  cand.push({ t: max, text: endLabel(end, partialEnd), anchor: 'end', prio: 0 });

  // Compressed segment: yearly step that leaves room for a 4-digit label.
  const segEnd = breakDate ? breakDate.getTime() : max;
  const segEndX = breakDate ? scale.breakX : right;
  const yearPx = scale.x(min + 365.25 * 864e5) - left;
  const need = 4 * charWidth + gap * 2;
  const step = [1, 2, 3, 5, 10].find(s => s * yearPx >= need) || 10;
  const y0 = new Date(min).getFullYear() + 1;
  for (let y = Math.ceil(y0 / step) * step; ; y += step) {
    const t = new Date(y, 0, 1).getTime();
    if (t >= segEnd) break;
    if (scale.x(t) > segEndX - need / 2) break;
    cand.push({ t, text: String(y), anchor: 'middle', prio: 2 });
  }

  // Stretched segment: quarterly.
  if (breakDate) {
    for (let d = new Date(breakDate); d.getTime() < max; d = new Date(d.getFullYear(), d.getMonth() + 3, 1)) {
      const t = d.getTime();
      const first = t === breakDate.getTime();
      let text;
      if (d.getMonth() === 0) text = String(d.getFullYear());
      else if (first) text = `${MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`;
      else text = MONTHS[d.getMonth()];
      cand.push({ t, text, anchor: 'middle', prio: first || d.getMonth() === 0 ? 1 : 3 });
    }
  }

  const ticks = cand.map(c => scale.x(c.t));
  const extent = c => {
    const x = scale.x(c.t);
    const w = c.text.length * charWidth;
    if (c.anchor === 'end') return [x - w, x];
    if (c.anchor === 'start') return [x, x + w];
    return [x - w / 2, x + w / 2];
  };
  const kept = [];
  for (const c of [...cand].sort((a, b) => a.prio - b.prio || a.t - b.t)) {
    const [a, b] = extent(c);
    if (a < left - 2 * charWidth || b > right + 1) continue;
    if (kept.some(k => a < k.ext[1] + gap && b > k.ext[0] - gap)) continue;
    kept.push({ ...c, ext: [a, b] });
  }
  kept.sort((a, b) => a.t - b.t);
  return {
    labels: kept.map(k => ({ t: k.t, x: scale.x(k.t), text: k.text, anchor: k.anchor })),
    ticks,
  };
}

/** Short note for the stretched band, e.g. "last 18 mo · ×3.3 scale" (or shorter if `px` is tight). */
export function stretchNote(scale, px = Infinity, charWidth = 4.6) {
  if (!scale.breakDate) return '';
  const f = `×${scale.factor.toFixed(1)}`;
  for (const s of [`last ${scale.recentMonths} months · ${f} time scale`, `last ${scale.recentMonths} mo · ${f} scale`, `${f} scale`, f]) {
    if (s.length * charWidth <= px) return s;
  }
  return '';
}
